import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCollect } from 'hooks';
import { safeParse } from 'utils/Helpers';

import { MATERIAL_TYPES } from 'utils/Constants';

import Show from 'components/Show';
import Editor from 'components/Editor';
import Pill from 'components/Misc/Pill';
import Icon from 'components/Misc/Icon';
import Progress from 'components/Misc/Progress';
import Container from 'components/Misc/Container';
import { Title } from 'components/Misc/Typography';

import Section from 'containers/Student/Materials/Profile/Section';

import { useAppStore } from 'store/useAppStore';

const Profile = () => {
	// Cache current object's id
	const { id } = useParams();

	// Use app's store
	const setTitle = useAppStore(state => state.setTitle);

	// Store requested material
	const [[material, progress], collect] = useCollect(`materials/${id}`);

	// Store material's table of contents
	const toc = useMemo(() => {
		const elements = safeParse(material?.content);
		return elements?.filter(element => ['heading-one', 'heading-two', 'heading-three'].includes(element.type))?.map(element => ({
			id: element?.id,
			type: element?.type,
			title: element?.children?.[0]?.text
		}));
	}, [material]);

	// Find type info
	const type = useMemo(() => MATERIAL_TYPES.find(type => type.value === material?.type), [material]);

	// Set page's title
	useEffect(() => setTitle(material?.titlePublic), [material]);

	// Collect material on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	return (
		<Container className="mx-auto max-w-screen-md p-4 space-y-4">
			{/* Show progress */}
			<Progress progress={progress} />

			{/* Show collected material */}
			<Show if={material?.id && !progress}>
				{/* Material's information */}
				<div className="flex items-center space-x-2">
					<Title
						title={material?.title}
						className="flex-1 text-xl"
					/>

					<Pill name={type?.text} />
				</div>
			
				{/* Material's table of contents */}
				<Show if={material?.type === 'document' && toc?.length}>
					<div className="space-y-2 flex flex-col">
						<Title
							title="Satura rādītājs"
							className="text-xl"
						/>

						{toc?.map(heading =>
							<Section key={heading.id} heading={heading} />
						)}
					</div>
				</Show>

				{/* Material's video */}
				<Show if={material?.type === 'video' && material?.video}>
					<video controls className="w-full rounded-lg">
						<source src={material?.video} />
					</video>
				</Show>

				{/* Material's file */}
				<Show if={material?.type === 'file' && material?.file}>
					<a
						download
						target="_blank"
						rel="noreferrer"
						href={`${process.env.REACT_APP_CDN}/materials/${material?.slug}/${material?.file}`}
						className="py-2 px-3 space-x-2 flex items-center rounded-lg text-neutral-800 dark:text-white bg-neutral-50 dark:bg-neutral-700 group cursor-pointer transition-colors"
					>
						<Icon
							icon="file_download"
							className="text-3xl text-neutral-800 dark:text-white group-hover:text-amber-500 transition-colors"
						/>

						<span>
							Lejupielādēt pievienoto failu
						</span>
					</a>
				</Show>
			
				{/* Material's content */}
				<Show if={material?.content}>
					<Editor
						readOnly
						path="materials"
						folder={material?.slug}
						value={material?.content}
						quiz={{
							mode: 'view'
						}}
					/>
				</Show>
			</Show>
		</Container>
	);
};

export default Profile;